import axios from 'axios';
import { store } from '../store';
import { updateToken, clearAuth } from '../store/slices/authSlice';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
  timeout: 30000,
  withCredentials: true,  // sends HttpOnly refresh token cookie
});

// Attach access token from Redux memory on every request
apiClient.interceptors.request.use((config) => {
  const { accessToken } = store.getState().auth;
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

// Auto-refresh on 401 — retries original request with new token
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_AUTH_SERVICE_URL}/v1/auth/refresh`,
          {},
          { withCredentials: true }
        );
        store.dispatch(updateToken(data.access_token));
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(original);
      } catch {
        store.dispatch(clearAuth());
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Service-specific clients (direct port calls — no gateway in Phase 1)
export const authApi = axios.create({ baseURL: import.meta.env.VITE_AUTH_SERVICE_URL, timeout: 30000, withCredentials: true });
export const employeeApi = axios.create({ baseURL: import.meta.env.VITE_EMPLOYEE_SERVICE_URL, timeout: 30000 });
export const attendanceApi = axios.create({ baseURL: import.meta.env.VITE_ATTENDANCE_SERVICE_URL, timeout: 30000 });
export const payrollApi = axios.create({ baseURL: import.meta.env.VITE_PAYROLL_ENGINE_URL, timeout: 30000 });
export const leaveApi = axios.create({ baseURL: import.meta.env.VITE_LEAVE_SERVICE_URL, timeout: 30000 });
export const expenseApi = axios.create({ baseURL: import.meta.env.VITE_EXPENSE_SERVICE_URL, timeout: 30000 });
export const reportApi = axios.create({ baseURL: import.meta.env.VITE_REPORT_SERVICE_URL, timeout: 60000 });
export const complianceApi = axios.create({ baseURL: import.meta.env.VITE_COMPLIANCE_ENGINE_URL, timeout: 60000 });
export const chatApi = axios.create({ baseURL: import.meta.env.VITE_CHAT_SERVICE_URL, timeout: 30000 });
export const lensApi = axios.create({ baseURL: import.meta.env.VITE_LENS_SYNC_SERVICE_URL, timeout: 30000 });
export const recruitmentApi = axios.create({ baseURL: import.meta.env.VITE_RECRUITMENT_SERVICE_URL, timeout: 30000 });
export const fileApi = axios.create({ baseURL: import.meta.env.VITE_FILE_SERVICE_URL, timeout: 60000 });
export const subscriptionApi = axios.create({ baseURL: import.meta.env.VITE_SUBSCRIPTION_SERVICE_URL, timeout: 30000 });

// Attach Bearer token from Redux to all service-specific clients
const attachToken = (config) => {
  const { accessToken } = store.getState().auth;
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
};

// authApi needs the token too for authenticated routes (admin/*, plan-request,
// profile, etc.) — safe no-op for the pre-login OTP calls that don't need it.
[authApi, employeeApi, attendanceApi, payrollApi, leaveApi, expenseApi, reportApi, complianceApi, chatApi, lensApi, recruitmentApi, fileApi, subscriptionApi]
  .forEach(client => client.interceptors.request.use(attachToken));
